/**
 * Who is on a chat widget right now — counts, and arrivals.
 *
 * Super Admin has no notification bell and this still does not add one — a second
 * inbox to check is not what "somebody is here" needs. The count lives on the
 * Live Chats nav item and an arrival fires a toast, a chime and (when the portal
 * is in a background tab) an OS notification, so the signal reaches whoever is
 * logged in without adding a surface that has to be tended.
 *
 * `recent` is deliberately a short, in-memory list scoped to this connection
 * rather than anything persisted. It exists so a toast has something behind it,
 * not so anyone has to work through it.
 */
import { create } from 'zustand';
import type { ChatVisitorAlertDto } from '@respectly/shared-types';

/** Where the mute preference lives, following the `respectly_*` convention. */
const SOUND_KEY = 'respectly_chat_sound';
/** Enough recent arrivals to glance at. This is a signal, not an inbox. */
const RECENT_LIMIT = 10;

/** Default on: an alert nobody configured still has to be noticeable. */
function storedSoundPreference(): boolean {
  try {
    return window.localStorage.getItem(SOUND_KEY) !== 'off';
  } catch {
    // Private mode, or storage disabled by policy.
    return true;
  }
}

interface ChatQueueState {
  waiting: number;
  agent: number;
  connected: boolean;
  /** Null until the first event; used to tell "no one waiting" from "not yet known". */
  lastAt: string | null;
  /** Newest first. Who has turned up on a widget since this session connected. */
  recent: ChatVisitorAlertDto[];
  soundEnabled: boolean;
  set: (counts: { waiting: number; agent: number; at: string }) => void;
  setConnected: (connected: boolean) => void;
  pushVisitor: (alert: ChatVisitorAlertDto) => void;
  setSoundEnabled: (enabled: boolean) => void;
}

export const useChatQueueStore = create<ChatQueueState>((set) => ({
  waiting: 0,
  agent: 0,
  connected: false,
  lastAt: null,
  recent: [],
  soundEnabled: storedSoundPreference(),
  set: (counts) => set({ waiting: counts.waiting, agent: counts.agent, lastAt: counts.at }),
  setConnected: (connected) => set({ connected }),
  pushVisitor: (alert) =>
    set((state) => ({
      // Keyed on the session, so a visitor who opens the panel and then types
      // stays one entry rather than becoming two.
      recent: [alert, ...state.recent.filter((a) => a.sessionId !== alert.sessionId)].slice(
        0,
        RECENT_LIMIT
      ),
    })),
  setSoundEnabled: (enabled) => {
    try {
      window.localStorage.setItem(SOUND_KEY, enabled ? 'on' : 'off');
    } catch {
      // Preference lasts for this tab only. Better than refusing to toggle.
    }
    set({ soundEnabled: enabled });
  },
}));
